/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState } from 'react';
import { cssMap, jsx } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/migration/chevron-up';
import ItemDropdownToggle from './item-dropdown-toggle';
import SwitcherThemedItem from './themed-item';
import SwitcherThemedChildItem from './themed-child-item';
import { token } from '@atlaskit/tokens';
import Avatar from './avatar';
import { FadeIn } from './fade-in';
import { type SwitcherChildItem } from '../../types';
import {
	createAndFireNavigationEvent,
	withAnalyticsEvents,
	UI_EVENT_TYPE,
	SWITCHER_CHILD_ITEM_SUBJECT,
	SWITCHER_ITEM_SUBJECT,
	SWITCHER_ITEM_EXPAND_SUBJECT,
	type WithAnalyticsEventsProps,
} from '../../common/utils/analytics';
import { type LozengeType } from './item';
import { useIsSwitcherTeam25TestCohort } from '../components/experiment-provider';
import { type ExperimentNudgeToJira, useNudgeToJira } from '../../common/utils/nudge-to-jira';

const styles = cssMap({
	itemContainer: {
		display: 'flex',
		boxSizing: 'border-box',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	refreshedItemContainer: {
		borderRadius: token('border.radius.100'),
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	childItemsContainer: {
		marginTop: token('space.025'),
		marginRight: 0,
		marginBottom: token('space.025'),
		marginLeft: 0,
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('border.radius'),
		overflow: 'hidden',
		padding: 0,
	},
	refreshedChildItemsContainer: {
		marginTop: token('space.025'),
		marginRight: 0,
		marginBottom: token('space.025'),
		marginLeft: 0,
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		borderRadius: token('border.radius.100'),
		padding: 0,
	},
	themeableItemWrapper: {
		width: '100%',
		borderRadius: token('border.radius'),
		height: token('space.600'),
		overflow: 'hidden',
	},
	refreshedThemeableItemWrapper: {
		width: '100%',
		borderRadius: token('border.radius'),
		height: token('space.600'),
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Ignored via go/DSP-18766
		marginRight: '-64px',
	},
	hoverColor: {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	zIndex: {
		zIndex: 1,
	},
});

interface Props extends WithAnalyticsEventsProps {
	children: React.ReactNode;
	icon: React.ReactNode;
	productName: React.ReactNode;
	shouldShowNewLozenge?: boolean;
	shouldShowNewBoldLozenge?: boolean;
	shouldShowTryLozenge?: boolean;
	lozenges?: LozengeType[];
	description?: React.ReactNode;
	onChildItemClick?: (e: React.SyntheticEvent) => void;
	onExpandClick?: () => void;
	onItemClick?: (e: React.SyntheticEvent) => void;
	href?: string;
	isDisabled?: boolean;
	childItems?: SwitcherChildItem[];
	testId?: string;
	experimentNudgeToJira?: ExperimentNudgeToJira;
}

function SwitcherItemWithDropDown({
	icon,
	description,
	childItems,
	onItemClick,
	onChildItemClick,
	shouldShowNewLozenge,
	shouldShowNewBoldLozenge,
	shouldShowTryLozenge,
	onExpandClick,
	productName,
	experimentNudgeToJira,
	...rest
}: Props) {
	const [showChildItems, setShowChildItems] = useState(false);
	const [isHovered, setHovered] = useState(false);
	const childItemsExist = childItems && childItems.length > 0;
	const productDescription =
		childItemsExist || shouldShowNewLozenge || shouldShowTryLozenge || shouldShowNewBoldLozenge
			? description
			: null;

	const isVisualRefreshEnabled = useIsSwitcherTeam25TestCohort();
	useNudgeToJira(experimentNudgeToJira);

	return (
		<FadeIn tag="li">
			<React.Fragment>
				<div css={[isVisualRefreshEnabled ? styles.refreshedItemContainer : styles.itemContainer]}>
					<div
						css={[
							isVisualRefreshEnabled
								? styles.refreshedThemeableItemWrapper
								: styles.themeableItemWrapper,
							isVisualRefreshEnabled && isHovered && styles.hoverColor,
						]}
					>
						<SwitcherThemedItem
							icon={icon}
							description={productDescription}
							onClick={onItemClick}
							{...rest}
						/>
					</div>
					{childItemsExist && (
						// eslint-disable-next-line -- Ignored via go/DSP-18766
						<div
							css={styles.zIndex}
							onMouseOver={() => setHovered(true)}
							onMouseOut={() => setHovered(false)}
							onFocus={() => setHovered(true)}
							onBlur={() => setHovered(false)}
						>
							<ItemDropdownToggle
								icon={
									<span aria-hidden="true">
										{showChildItems ? (
											<ChevronUpIcon color="currentColor" label="Collapse" size="small" />
										) : (
											<ChevronDownIcon color="currentColor" label="Expand" size="small" />
										)}
									</span>
								}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setShowChildItems(!showChildItems);
									if (!showChildItems) {
										onExpandClick && onExpandClick();
									}
								}}
								expanded={showChildItems}
								productName={productName}
								numSites={childItems.length}
							/>
						</div>
					)}
				</div>
				{showChildItems && childItems && (
					// eslint-disable-next-line @atlaskit/design-system/prefer-primitives
					<ul
						css={[
							isVisualRefreshEnabled
								? styles.refreshedChildItemsContainer
								: styles.childItemsContainer,
						]}
					>
						{childItems.map((item) => (
							<SwitcherThemedChildItem
								tag="li"
								icon={<Avatar avatarUrl={item.avatar} alt="" />}
								href={item.href}
								key={item.label}
								onClick={onChildItemClick}
								testId="switcher-child-item"
							>
								{item.label}
							</SwitcherThemedChildItem>
						))}
					</ul>
				)}
			</React.Fragment>
		</FadeIn>
	);
}

const SwitcherItemWithDropDownWithEvents = withAnalyticsEvents({
	onChildItemClick: createAndFireNavigationEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: SWITCHER_CHILD_ITEM_SUBJECT,
	}),
	onExpandClick: createAndFireNavigationEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: SWITCHER_ITEM_EXPAND_SUBJECT,
	}),
	onItemClick: createAndFireNavigationEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: SWITCHER_ITEM_SUBJECT,
	}),
})(SwitcherItemWithDropDown);

export default SwitcherItemWithDropDownWithEvents;
